'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Container, IconButton, Typography } from '@mui/material'
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion'

// Types
interface SlideData {
  IMAGE: string
  JUDUL: string
  KETERANGAN: string
}

interface ImageSliderProps {
  imageSliderList: SlideData[]
  autoPlayDelay?: number
  swipeThreshold?: number
}

// Constants
const SLIDE_DURATION = 10000
const SWIPE_THRESHOLD = 50
const BASE_IMAGE_URL = process.env.NEXT_PUBLIC_URL_IMAGE_SLIDER

const ImageSlider = ({
  imageSliderList,
  autoPlayDelay = SLIDE_DURATION,
  swipeThreshold = SWIPE_THRESHOLD
}: ImageSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [isUserInteracting, setIsUserInteracting] = useState(false)

  const containerRef = useRef<HTMLElement>(null)

  // Scroll animation
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  })

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1])
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.7])

  // Navigation handlers
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % imageSliderList.length)
  }, [imageSliderList.length])

  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + imageSliderList.length) % imageSliderList.length)
  }, [imageSliderList.length])

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index)
  }, [])

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX)
    setIsUserInteracting(true)
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStart === null) return

      const touchEnd = e.changedTouches[0].clientX
      const diff = touchStart - touchEnd

      if (Math.abs(diff) > swipeThreshold) {
        diff > 0 ? goToNext() : goToPrev()
      }

      setTouchStart(null)
      setIsUserInteracting(false)
    },
    [touchStart, swipeThreshold, goToNext, goToPrev]
  )

  // Preload next image
  useEffect(() => {
    const nextIndex = (currentIndex + 1) % imageSliderList.length
    const nextImage = new Image()

    nextImage.src = `${BASE_IMAGE_URL}${imageSliderList[nextIndex].IMAGE}`
  }, [currentIndex, imageSliderList])

  // Auto-play effect
  useEffect(() => {
    if (isUserInteracting) return

    const timer = setInterval(goToNext, autoPlayDelay)

    
return () => clearInterval(timer)
  }, [goToNext, autoPlayDelay, isUserInteracting])

  // Current slide data
  const currentSlide = useMemo(() => imageSliderList[currentIndex], [imageSliderList, currentIndex])

  // Format title helper
  const formatTitle = useCallback(
    (text: string) => text.toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase()),
    []
  )

  // Split long text into paragraphs
  const formatDescription = useCallback((text: string) => {
    const sentences = text.split('.').filter(s => s.trim())
    const midPoint = Math.ceil(sentences.length / 2)

    return {
      firstPart: sentences.slice(0, midPoint).join('. ') + (sentences.length > 1 ? '.' : ''),
      secondPart: sentences.length > midPoint ? sentences.slice(midPoint).join('. ') + '.' : ''
    }
  }, [])

  if (!imageSliderList?.length) {
    return null
  }

  const { firstPart, secondPart } = formatDescription(currentSlide.KETERANGAN)

  return (
    <Box
      ref={containerRef}
      component='section'
      role='banner'
      aria-label='Hero Image Slider'
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '100vh', md: '90vh' },
        overflow: 'hidden',
        isolation: 'isolate'
      }}
      id='hero-slider'
    >
      <AnimatePresence mode='wait' initial={false}>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            inset: 0,
            scale,
            opacity
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Background Image */}
          <Box
            component='img'
            src={`${BASE_IMAGE_URL}${currentSlide.IMAGE}`}
            alt={currentSlide.JUDUL}
            loading={currentIndex === 0 ? 'eager' : 'lazy'}
            draggable={false}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              userSelect: 'none'
            }}
          />

          {/* Dark Overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
              zIndex: 1
            }}
          />

          {/* Content */}
          <Container
            maxWidth='lg'
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
              px: { xs: 3, md: 4 },
              textAlign: 'center'
            }}
          >
            <div style={{ width: '100%', maxWidth: '900px' }}>
              {/* Main Title */}
              <Typography
                component='h1'
                sx={{
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                  fontSize: {
                    xs: '1.1rem',
                    sm: '1.4rem',
                    md: '1.7rem',
                    lg: '2rem'
                  },
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: '-0.02em',
                  color: 'white',
                  textShadow: '0 4px 20px rgba(0,0,0,0.8)',
                  mb: { xs: 3, md: 4 },
                  textTransform: 'uppercase'
                }}
              >
                {currentSlide.JUDUL}
              </Typography>
              {/* Description Container */}
              <Box
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  p: { xs: '1.2rem', md: '2rem' },
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}
              >
                <Typography
                  component='p'
                  sx={{
                    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                    fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                    fontWeight: 400,
                    lineHeight: 1.7,
                    color: 'rgba(255, 255, 255, 0.95)',
                    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    mb: secondPart ? 2 : 0
                  }}
                >
                  {firstPart}
                </Typography>

                {secondPart && (
                  <Typography
                    component='p'
                    sx={{
                      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                      fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                      fontWeight: 400,
                      lineHeight: 1.7,
                      color: 'rgba(255, 255, 255, 0.9)',
                      textShadow: '0 2px 8px rgba(0,0,0,0.5)'
                    }}
                  >
                    {secondPart}
                  </Typography>
                )}
              </Box>
            </div>
          </Container>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Controls */}
      {imageSliderList.length > 1 && (
        <>
          <IconButton
            onClick={goToPrev}
            onMouseEnter={() => setIsUserInteracting(true)}
            onMouseLeave={() => setIsUserInteracting(false)}
            aria-label='Previous image'
            sx={{
              position: 'absolute',
              left: { xs: 16, md: 32 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              '&:hover': {
                bgcolor: 'white',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }}
          >
            <ChevronLeft sx={{ color: 'primary.main', fontSize: { xs: 28, md: 32 } }} />
          </IconButton>

          <IconButton
            onClick={goToNext}
            onMouseEnter={() => setIsUserInteracting(true)}
            onMouseLeave={() => setIsUserInteracting(false)}
            aria-label='Next image'
            sx={{
              position: 'absolute',
              right: { xs: 16, md: 32 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              '&:hover': {
                bgcolor: 'white',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }}
          >
            <ChevronRight sx={{ color: 'primary.main', fontSize: { xs: 28, md: 32 } }} />
          </IconButton>

          {/* Slide Indicators */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 24, md: 40 },
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: { xs: 8, md: 12 },
              zIndex: 3,
              p: { xs: '8px 16px', md: '12px 20px' },
              borderRadius: '50px',
              bgcolor: 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
            role='tablist'
            aria-label='Slide indicators'
          >
            {imageSliderList.map((_, index) => (
              <Box
                key={index}
                role='tab'
                tabIndex={0}
                aria-selected={index === currentIndex}
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => goToSlide(index)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    goToSlide(index)
                  }
                }}
                sx={{
                  width: index === currentIndex ? { xs: 32, md: 40 } : { xs: 12, md: 16 },
                  height: { xs: 12, md: 16 },
                  borderRadius: { xs: 6, md: 8 },
                  bgcolor: index === currentIndex ? 'white' : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.8)',
                    transform: 'scale(1.1)'
                  },
                  '&:focus': {
                    outline: '2px solid white',
                    outlineOffset: '4px'
                  }
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}

export default ImageSlider
